import { Clock, User, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { NextGenImage } from '@/components/NextGenImage';
import { useEffect } from 'react';
import mechanicWorkingOptimized from '@/assets/mechanic-working-optimized.webp';
import diagnosticServiceOptimized from '@/assets/diagnostic-service-optimized.webp';
import mechanicDiagnosticsOptimized from '@/assets/mechanic-diagnostics-optimized.webp';
import professionalToolsOptimized from '@/assets/professional-tools-optimized.webp';

const blogPosts = [
  {
    id: 1,
    title: "Mobile Headlight Restoration: From Foggy to Crystal Clear in Your Driveway",
    excerpt: "Cloudy headlights reduce light output by 78% and fail safety inspections. Our mobile team restores factory clarity using professional techniques at your location.",
    image: mechanicWorkingOptimized,
    author: "Mike Johnson",
    date: "December 15, 2024",
    readTime: "8 min read",
    category: "Mobile Service"
  },
  {
    id: 2,
    title: "The 50-Cent Battery Terminal Fix That Prevents Emergency Callouts",
    excerpt: "Corroded battery terminals cause 17 million roadside assistance calls annually. Learn the simple household solution our mobile mechanics use to prevent breakdowns.",
    image: diagnosticServiceOptimized,
    author: "Sarah Williams",
    date: "December 12, 2024",
    readTime: "6 min read",
    category: "Preventive Care"
  },
  {
    id: 3,
    title: "Stop Topping Up Coolant: Why This 'Quick Fix' Destroys Engines",
    excerpt: "25% of major engine failures stem from coolant system neglect. Our diagnostic service reveals why adding coolant without fixing leaks costs thousands in repairs.",
    image: mechanicDiagnosticsOptimized,
    author: "David Thompson",
    date: "December 10, 2024",
    readTime: "9 min read",
    category: "Diagnostics"
  },
  {
    id: 4,
    title: "Gold Coast Heat Alert: Why AC and Overheating Go Hand in Hand",
    excerpt: "AC-related overheating spikes 90% during Gold Coast summers. Our mobile mechanics explain the engineering relationship and prevention strategies for local drivers.",
    image: professionalToolsOptimized,
    author: "Lisa Chen",
    date: "December 8, 2024",
    readTime: "7 min read",
    category: "Climate Care"
  }
];

export const BlogSection = () => {
  // Preload first two images for better performance
  useEffect(() => {
    const preloadImage = (src: string) => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      link.fetchPriority = 'high';
      document.head.appendChild(link);
    };

    // Preload first two blog images
    preloadImage(mechanicWorkingOptimized);
    preloadImage(diagnosticServiceOptimized);
  }, []);

  return (
    <section 
      id="blog" 
      className="py-24 relative overflow-hidden bg-gradient-to-br from-primary/5 via-background to-muted/10"
    >
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 hero-pattern opacity-20"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="section-header">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary to-primary-hover rounded-2xl mb-8 shadow-[var(--shadow-glow)]">
            <User className="w-10 h-10 text-white" />
          </div>
          <h2 className="section-title">
            Expert Automotive Insights & Tips
          </h2>
          <p className="section-subtitle leading-relaxed mb-8 text-center">
            Stay informed with professional advice, maintenance tips, and industry insights 
            from our certified mechanical team on the Gold Coast.
          </p>
          <div className="section-divider" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {blogPosts.map((post, index) => (
            <Card key={post.id} className="service-card group hover:shadow-[var(--shadow-glow)] transition-all duration-300 overflow-hidden border-0">
              <div className="relative overflow-hidden">
                <NextGenImage
                  src={post.image}
                  alt={`${post.title} - ${post.category} automotive guide`}
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                  priority={index < 2}
                  loading={index < 2 ? "eager" : "lazy"}
                  fetchPriority={index < 2 ? "high" : "low"}
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute top-6 left-6">
                  <span className="bg-primary/90 backdrop-blur-sm text-white text-sm font-semibold px-4 py-2 rounded-full shadow-lg">
                    {post.category}
                  </span>
                </div>
                <div className="absolute bottom-6 right-6">
                  <div className="bg-card/90 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg">
                    <span className="text-sm font-semibold text-foreground">{post.readTime}</span>
                  </div>
                </div>
              </div>
              
              <CardHeader className="pb-4">
                <h3 className="text-2xl font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2 leading-tight">
                  {post.title}
                </h3>
              </CardHeader>
              
              <CardContent className="pt-0">
                <p className="text-muted-foreground mb-8 line-clamp-3 leading-loose font-medium text-justify">
                  {post.excerpt}
                </p>
                
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-border/30">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <span className="font-semibold text-foreground">{post.author}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">{post.date}</span>
                    </div>
                  </div>
                </div>
                
                <Link to="/blog">
                  <Button variant="ghost" className="w-full group/btn hover:bg-primary/10 border border-primary/20 hover:border-primary/40 transition-all duration-300">
                    <span className="font-semibold">Read Complete Article</span>
                    <ChevronRight className="w-5 h-5 ml-2 group-hover/btn:translate-x-1 transition-transform duration-300" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-16">
          <div className="bg-card/80 backdrop-blur-sm border-2 border-border/50 rounded-2xl p-8 shadow-[var(--shadow-elegant)] hover:shadow-[var(--shadow-glow)] transition-all duration-300">
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Want More Automotive Tips?
            </h3>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto leading-loose text-center">
              Explore our complete library of automotive guides, maintenance tips, and industry insights to keep your vehicle running smoothly.
            </p>
            <Link to="/blog">
              <Button size="lg" className="btn-premium px-10 py-4 text-lg">
                Browse All Articles
                <ChevronRight className="w-6 h-6 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};